import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import Lightbox from 'yet-another-react-lightbox'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import VideoModal from '../ui/VideoModal'
import { posterUrl } from '../ui/videoSource'
import './user.css'

/*
 * MEDIA LIBRARY — what the player has published.
 *
 * GET manager/dashboard/userposts/{uuid} -> results: [{ UUID, Photo, Descr }]
 *
 * The old layout was a MUI <Box> holding a flex-wrapped run of 300px squares
 * with the caption floated over the bottom of each photo. It is a Panel and a
 * CSS grid now: the tiles reflow to the panel width instead of a fixed size,
 * and the caption sits below the image where it can be read against the page
 * rather than against whatever happens to be in the corner of the photo.
 *
 * The lightbox is untouched — it is the one part of this file doing work that
 * markup cannot do. Only posts that actually carry a photo enter the gallery,
 * and the tiles are built from that same filtered list, so the index a tile
 * opens is always the slide it shows.
 */

export default function UserPosts(params) {
  const [photos, setPhotos] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  // Posts carry videos too; those open in the shared VideoModal, not the lightbox.
  const [clips, setClips] = useState([])
  const [clipIndex, setClipIndex] = useState(-1)

  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    loadData()
    return () => { aliveRef.current = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.uuid])

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)

    axios
      .get(
        process.env.REACT_APP_API_SERVER +
        'manager/dashboard/userposts/' +
        params.uuid,
        {
          headers: {
            Authorization: 'Bearer ' + sessionStorage.getItem('mngapiToken'),
          },
        }
      )
      .then((res) => {
        const postsData = (res.data && Array.isArray(res.data.results)) ? res.data.results : []

        // One list drives both the grid and the lightbox, so tile n is slide n.
        const withPhoto = postsData
          .filter((item) => item && item.Photo && String(item.Photo).length > 0)
          .map((item) => ({
            uuid: item.UUID,
            src: process.env.REACT_APP_API_SERVER + 'postfiles/' + item.UUID + '/123',
            descr: item.Descr ? String(item.Descr) : '',
          }))

        // VideoID arrives WITH an extension ("OTH_123.mov"); videoSource strips it.
        const withVideo = postsData
          .filter((item) => item && item.VideoID && String(item.VideoID).length > 0)
          .map((item) => ({
            uuid: item.UUID,
            fileId: item.VideoID,
            descr: item.Descr ? String(item.Descr) : '',
          }))

        if (!aliveRef.current) return
        setPhotos(withPhoto)
        setClips(withVideo)
        setIsLoading(false)
      })
      .catch((err) => {
        if (!aliveRef.current) return
        setIsLoading(false)
        setFailed(true)
      })
  }

  const playerName = params.name || 'This player'

  const clipTiles = clips.map((item, index) => (
    <button
      type="button"
      className="usr-shot usr-clip"
      key={item.uuid || 'clip-' + index}
      onClick={() => setClipIndex(index)}
      aria-label={item.descr ? 'Play video: ' + item.descr : 'Play video ' + (index + 1)}
    >
      <span className="usr-shot-img">
        <img src={posterUrl(item.fileId)} alt="" loading="lazy" />
        <span className="usr-clip-play" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M8 5.2v13.6L19 12 8 5.2z" /></svg>
        </span>
      </span>
      <span className={item.descr ? 'usr-shot-cap' : 'usr-shot-cap usr-shot-cap-none'}>
        {item.descr || 'No caption'}
      </span>
    </button>
  ))

  const handleImageClick = (index) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const slides = photos.map((item) => ({
    src: item.src,
    title: item.descr,
    description: item.descr,
  }))

  const tiles = photos.map((item, index) => (
    <button
      type="button"
      className="usr-shot"
      key={item.uuid || index}
      onClick={() => handleImageClick(index)}
      aria-label={item.descr ? 'Open photo: ' + item.descr : 'Open photo ' + (index + 1)}
    >
      <span className="usr-shot-img">
        <img src={item.src} alt={item.descr || ''} loading="lazy" />
      </span>
      <span className={item.descr ? 'usr-shot-cap' : 'usr-shot-cap usr-shot-cap-none'}>
        {item.descr || 'No caption'}
      </span>
    </button>
  ))

  return (
    <Panel
      title="Media Library"
      subtitle={!isLoading && (photos.length > 0 || clips.length > 0)
        ? [
            photos.length > 0 ? photos.length + (photos.length === 1 ? ' photo' : ' photos') : '',
            clips.length > 0 ? clips.length + (clips.length === 1 ? ' video' : ' videos') : '',
          ].filter(Boolean).join(' · ') + ' published'
        : 'Photos and videos published by this player'}
    >

      {isLoading && (
        <div className="usr-media-grid">
          <div className="skeleton usr-shot-img"></div>
          <div className="skeleton usr-shot-img"></div>
          <div className="skeleton usr-shot-img"></div>
          <div className="skeleton usr-shot-img"></div>
        </div>
      )}

      {!isLoading && failed && (
        <div className="usr-alert" role="alert">
          <b>The media library could not be loaded.</b>
          <span>The server did not answer. The rest of the profile is unaffected.</span>
          <Btn onClick={loadData}>Try again</Btn>
        </div>
      )}

      {!isLoading && !failed && photos.length === 0 && clips.length === 0 && (
        <div className="empty">This player has not published any photos or videos yet.</div>
      )}

      {!isLoading && !failed && clips.length > 0 && (
        <>
          <div className="mono usr-media-label">Videos</div>
          <div className="usr-media-grid">{clipTiles}</div>
        </>
      )}

      {!isLoading && !failed && photos.length > 0 && (
        <>
          {clips.length > 0 && <div className="mono usr-media-label">Photos</div>}
          <div className="usr-media-grid">{tiles}</div>
        </>
      )}

      {clipIndex >= 0 && clipIndex < clips.length && (
        <VideoModal
          items={clips.map((item) => ({
            fileId: item.fileId,
            name: playerName,
            uuid: params.uuid,
            subtitle: item.descr || 'Published video',
          }))}
          index={clipIndex}
          title={playerName}
          onIndex={setClipIndex}
          onClose={() => setClipIndex(-1)}
        />
      )}

      {lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={slides}
          index={lightboxIndex}
          animation={{ fade: 500 }}
          plugins={[Captions]}
          captions={{
            hidden: false,
            showToggle: true,
            descriptionTextAlign: 'center',
          }}
        />
      )}

    </Panel>
  )
}
